import React from "react";
import { MapPin, Phone, Clock, Mail, MessageCircle, ArrowRight } from "lucide-react";

export default function Contact() {
  return (
    <section id="contact" className="relative py-24 lg:py-32 bg-[#FAF8F5]" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        {/* Header */}
        <div className="max-w-2xl mb-12">
          <p className="eyebrow mb-4">Visit · Call · Write</p>
          <h2 className="font-display text-4xl md:text-5xl text-slate-900 font-medium leading-[1.05] tracking-tight">
            Our Display Studio in <span className="italic text-amber-700">Bengaluru.</span>
          </h2>
          <p className="mt-4 text-slate-600 max-w-xl">
            Touch, walk and compare hundreds of premium flooring, wallpaper and carpet samples in our central Lalbagh studio.
          </p>
        </div>

        {/* Info grid (4 columns on desktop, stacked on mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <InfoTile
            icon={MapPin}
            label="Address"
            testid="contact-address"
            body={<>2/1, 3rd Cross, Lal Bagh Main Rd,<br />Bengaluru, Karnataka 560027</>}
          />
          <InfoTile
            icon={Phone}
            label="Phone"
            testid="contact-phone"
            body={
              <>
                <a href="tel:09845048406" className="block text-slate-900 hover:text-amber-700 transition-colors">098450 48406</a>
                <a href="tel:08041226464" className="block text-slate-900 hover:text-amber-700 transition-colors">080-41226464</a>
              </>
            }
          />
          <InfoTile
            icon={Mail}
            label="Email"
            testid="contact-email"
            body={
              <a href="mailto:gurunathenterprises@gmail.com" className="block text-slate-900 hover:text-amber-700 transition-colors break-all">
                gurunathenterprises@gmail.com
              </a>
            }
          />
          <InfoTile
            icon={Clock}
            label="Hours"
            testid="contact-hours"
            body={
              <>
                <p className="text-slate-900">Mon – Sat: 10:30 AM – 7:00 PM</p>
                <p className="text-slate-500 text-sm mt-1">Sunday Closed</p>
              </>
            }
          />
        </div>

        {/* Full-width Map below */}
        <div className="mt-10">
          <iframe
            title="Shri Gurunath Enterprises — Bengaluru Studio Location"
            src="https://www.google.com/maps?q=2/1,+3rd+Cross,+Lal+Bagh+Main+Rd,+Bengaluru,+Karnataka+560027&output=embed"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="w-full h-[300px] rounded-lg shadow-md border border-slate-200"
            style={{ border: 0 }}
            allowFullScreen
            data-testid="contact-map-embed"
          />
        </div>

        {/* Inline WhatsApp CTA below the map */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white border border-slate-200 px-6 py-5 rounded-lg" data-testid="contact-whatsapp-cta-card">
          <div>
            <p className="eyebrow !text-emerald-600">Fastest Response</p>
            <p className="mt-1 font-display text-xl text-slate-900">Get instant pricing & samples on WhatsApp</p>
          </div>
          <a
            href="https://wa.me/919845048406?text=Hi%2C%20I%27m%20interested%20in%20your%20wholesale%20flooring%20%26%20carpet%20range."
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 text-[12px] tracking-[0.22em] uppercase rounded-md transition-colors shrink-0"
            data-testid="contact-whatsapp-cta-button"
          >
            <MessageCircle className="h-4 w-4" />
            Chat on WhatsApp
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}

function InfoTile({ icon: Icon, label, body, testid }) {
  return (
    <div className="bg-white border border-slate-900/10 p-6" data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <span className="inline-flex h-10 w-10 items-center justify-center bg-amber-700/10 border border-amber-700/30 text-amber-700">
          <Icon className="h-4 w-4" />
        </span>
        <p className="eyebrow !text-slate-500">{label}</p>
      </div>
      <div className="text-slate-900 leading-relaxed text-sm">{body}</div>
    </div>
  );
}
